import { useState, useEffect } from 'react';
import { FileText, GraduationCap, Award, Calendar, AlertCircle, Info, ClipboardList } from 'lucide-react';
import { getStudentTranscript, TranscriptData, ElementGrade } from '../../../services/studentTranscriptService';
import SurveyModal from '../components/survey/SurveyModal';

interface SurveyModalInfo {
  moduleElementId: string;
  groupeId: string;
  academicYearId: string;
  elementName: string;
}

const TranscriptPage = () => {
  const [transcript, setTranscript] = useState<TranscriptData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [surveyModal, setSurveyModal] = useState<SurveyModalInfo | null>(null);

  useEffect(() => {
    loadTranscript();
  }, []);

  const loadTranscript = async () => {
    try {
      setLoading(true);
      const data = await getStudentTranscript();
      setTranscript(data);
    } catch (error) {
      console.error('Error loading transcript:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade: number | null) => {
    if (grade === null) return 'text-gray-400';
    if (grade >= 16) return 'text-green-600 font-semibold';
    if (grade >= 12) return 'text-blue-600 font-semibold';
    if (grade >= 10) return 'text-yellow-600 font-semibold';
    return 'text-red-600 font-semibold';
  };

  // Count pending surveys across all years
  const getPendingSurveys = (): { count: number; elements: { name: string; academicYear: string }[] } => {
    if (!transcript) return { count: 0, elements: [] };

    const pending: { name: string; academicYear: string }[] = [];
    for (const year of transcript.academicYears) {
      for (const semester of year.semesters) {
        for (const module of semester.modules) {
          for (const element of module.elements) {
            if (element.surveyRequired && !element.surveyCompleted) {
              pending.push({ name: element.elementName, academicYear: year.academicYearName });
            }
          }
        }
      }
    }
    return { count: pending.length, elements: pending };
  };

  const handleOpenSurvey = (
    element: ElementGrade,
    academicYearId: string
  ) => {
    if (element.surveyRequired && !element.surveyCompleted && element.groupeId) {
      setSurveyModal({
        moduleElementId: element.elementId,
        groupeId: element.groupeId,
        academicYearId,
        elementName: element.elementName,
      });
    }
  };

  const handleSurveyComplete = () => {
    setSurveyModal(null);
    // Refresh transcript to show grades after survey completion
    loadTranscript();
  };

  const pendingSurveys = getPendingSurveys();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 mt-4">Chargement du relevé de notes...</p>
        </div>
      </div>
    );
  }

  if (!transcript) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Relevé de notes non disponible
          </h3>
          <p className="text-gray-600">
            Aucune donnée de relevé de notes disponible pour le moment.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <GraduationCap className="w-8 h-8" />
          Relevé de Notes
        </h1>
        <p className="text-gray-600 mt-1">
          Consultez votre parcours académique et vos résultats
        </p>
      </div>

      {/* Student Info Card */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-6 mb-6 text-white">
        <div>
          <h2 className="text-2xl font-bold mb-2">
            {transcript.student.firstName} {transcript.student.lastName}
          </h2>
          <div className="space-y-1 text-blue-100">
            <p className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Code Étudiant: {transcript.student.studentCode}
            </p>
            <p className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4" />
              Filière: {transcript.student.filiere} ({transcript.student.filiereCode})
            </p>
            <p className="flex items-center gap-2">
              <Award className="w-4 h-4" />
              Campus: {transcript.student.campus}
            </p>
          </div>
        </div>
      </div>

      {/* Survey Required Banner */}
      {pendingSurveys.count > 0 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <ClipboardList className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-900">
                {pendingSurveys.count} sondage{pendingSurveys.count > 1 ? 's' : ''} à compléter
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                Vous devez compléter {pendingSurveys.count > 1 ? 'les sondages d\'évaluation' : 'le sondage d\'évaluation'} pour voir vos notes dans {pendingSurveys.count > 1 ? 'les matières suivantes' : 'la matière suivante'}:
              </p>
              <ul className="mt-2 space-y-1">
                {pendingSurveys.elements.map((el, idx) => (
                  <li key={idx} className="text-sm text-amber-800 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                    {el.name} ({el.academicYear})
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Year Filter */}
      <div className="mb-6 flex items-center gap-4">
        <label className="text-sm font-medium text-gray-700">Année académique:</label>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">Toutes les années</option>
          {transcript.academicYears.map((year) => (
            <option key={year.academicYearId} value={year.academicYearId}>
              {year.academicYearName}
            </option>
          ))}
        </select>
      </div>

      {/* Academic Years */}
      <div className="space-y-6">
        {transcript.academicYears
          .filter((year) => selectedYear === 'all' || year.academicYearId === selectedYear)
          .map((year) => (
            <div key={year.academicYearId} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* Year Header */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Année Académique {year.academicYearName}
                  </h3>
                </div>
              </div>

              {/* Semesters */}
              {year.semesters.map((semester) => (
                <div key={semester.semesterId} className="p-6 border-b border-gray-100">
                  {/* Semester Header */}
                  <div className="mb-4">
                    <h4 className="text-lg font-bold text-gray-900 bg-blue-600 text-white px-4 py-2 rounded">
                      {semester.semesterName}
                    </h4>
                  </div>

                  {/* Modules Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider border border-gray-300">
                            Module
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider border border-gray-300">
                            Matière (Élément)
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider border border-gray-300">
                            Note/20
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider border border-gray-300">
                            Coef.
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider border border-gray-300">
                            Moyenne Module
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {semester.modules.map((module, _moduleIdx) => (
                          <>
                            {module.elements.map((element, elementIdx) => (
                              <tr key={element.elementId} className="hover:bg-gray-50">
                                {elementIdx === 0 && (
                                  <td
                                    rowSpan={module.elements.length}
                                    className="px-4 py-3 align-middle border border-gray-300"
                                  >
                                    <div className="font-semibold text-gray-900">{module.moduleName}</div>
                                    <div className="text-xs text-gray-500 mt-0.5">{module.moduleCode}</div>
                                  </td>
                                )}
                                <td className="px-4 py-3 text-sm text-gray-700 border border-gray-300">
                                  <div className="flex items-center gap-2">
                                    {element.elementName}
                                    {element.isFromRattrapage && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                        Rattrapage
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-center border border-gray-300">
                                  {element.surveyRequired && !element.surveyCompleted ? (
                                    <button
                                      onClick={() => handleOpenSurvey(element, year.academicYearId)}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition-colors text-sm font-medium"
                                    >
                                      <ClipboardList size={14} />
                                      Sondage requis
                                    </button>
                                  ) : element.status === 'pending' ? (
                                    <span className="text-gray-400 text-sm italic">En cours</span>
                                  ) : element.status === 'absent' ? (
                                    <span className="text-red-600 text-sm font-medium">Absent</span>
                                  ) : (
                                    <span className={getGradeColor(element.average)}>
                                      {element.average !== null ? element.average.toFixed(2) : '--'}
                                    </span>
                                  )}
                                </td>
                                {elementIdx === 0 && (
                                  <>
                                    <td
                                      rowSpan={module.elements.length}
                                      className="px-4 py-3 text-center font-medium border border-gray-300"
                                    >
                                      {module.coefficient}
                                    </td>
                                    <td
                                      rowSpan={module.elements.length}
                                      className="px-4 py-3 text-center bg-blue-50 border border-gray-300"
                                    >
                                      <span className={`font-bold ${getGradeColor(module.average)}`}>
                                        {module.average !== null ? module.average.toFixed(2) : '--'}
                                      </span>
                                    </td>
                                  </>
                                )}
                              </tr>
                            ))}
                          </>
                        ))}
                        {/* Semester Summary Row */}
                        <tr className="bg-gray-100 font-bold">
                          <td colSpan={2} className="px-4 py-3 text-right border border-gray-300">
                            Moyenne {semester.semesterName}
                          </td>
                          <td colSpan={3} className="px-4 py-3 text-center border border-gray-300">
                            <span className={`text-lg ${getGradeColor(semester.average)}`}>
                              {semester.average !== null ? semester.average.toFixed(2) : 'En cours'}/20
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              {/* Annual Summary */}
              {year.annualAverage !== null && (
                <div className="p-6 bg-gradient-to-r from-green-50 to-blue-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-6">
                      <div>
                        <p className="text-sm text-gray-600 font-medium mb-1">Moyenne Générale</p>
                        <p className={`text-3xl font-bold ${getGradeColor(year.annualAverage)}`}>
                          {year.annualAverage.toFixed(2)}/20
                        </p>
                      </div>
                      {year.mention && (
                        <div>
                          <p className="text-sm text-gray-600 font-medium mb-1">Mention</p>
                          <div className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-green-500 rounded-lg">
                            <Award className="w-5 h-5 text-green-600" />
                            <span className="text-lg font-bold text-green-700">{year.mention}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
      </div>

      {/* Mentions Legend */}
      <div className="mt-8 bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Award className="w-5 h-5" />
          Légende des Mentions
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-green-100 text-green-600 flex items-center justify-center font-bold">
              16+
            </div>
            <div>
              <p className="font-medium text-gray-900">Très Bien</p>
              <p className="text-xs text-gray-500">≥ 16/20</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">
              14+
            </div>
            <div>
              <p className="font-medium text-gray-900">Bien</p>
              <p className="text-xs text-gray-500">14-16/20</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-yellow-100 text-yellow-600 flex items-center justify-center font-bold">
              12+
            </div>
            <div>
              <p className="font-medium text-gray-900">Assez Bien</p>
              <p className="text-xs text-gray-500">12-14/20</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center font-bold">
              10+
            </div>
            <div>
              <p className="font-medium text-gray-900">Passable</p>
              <p className="text-xs text-gray-500">10-12/20</p>
            </div>
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <p className="font-semibold mb-1">Relevé de notes non officiel</p>
          <p>
            Ce relevé de notes est fourni à titre informatif uniquement. Pour obtenir un relevé de notes
            officiel signé et tamponné par l'administration, veuillez faire une demande via la section
            <strong> "Mes Demandes"</strong>.
          </p>
        </div>
      </div>

      {/* Survey Modal */}
      {surveyModal && (
        <SurveyModal
          moduleElementId={surveyModal.moduleElementId}
          groupeId={surveyModal.groupeId}
          academicYearId={surveyModal.academicYearId}
          onClose={() => setSurveyModal(null)}
          onComplete={handleSurveyComplete}
        />
      )}
    </div>
  );
};

export default TranscriptPage;
